import { jsx } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { useParentBg } from "../../shared";
import { AvatarBox, AvatarImageContainer, PlaceholderImg } from "./Avatar.styled";
import { AvatarContext, useAvatarContext } from "./context/AvatarContext";
import { useAvatarColors } from "./hooks/useAvatarColors";
import { useImageLoad } from "./hooks/useImageLoad";
import { SlotAvatar } from "./slots/SlotAvatar";
import { SlotBadge, SlotBadgeOnline } from "./slots/SlotBadge";
import { SlotIcon } from "./slots/SlotIcon";
import { abbreviate } from "./utils";
import { Icon } from "../Icon";
import { useTheme } from "../ThemeProvider";
import Typography from "../Typography";
const AvatarRoot = (props) => {
  const {
    size = "l",
    shape = "square",
    gradient,
    color,
    children,
    onClick,
    ...rest
  } = props;
  const theme = useTheme();
  const parentBg = useParentBg() ?? "elevation0";
  const [imageLoaded, setImageLoaded] = useState(false);
  const value = useMemo(
    () => ({
      size,
      shape,
      color,
      gradient,
      parentBg,
      theme,
      imageLoaded,
      setImageLoaded
    }),
    [size, shape, color, gradient, parentBg, theme, imageLoaded, setImageLoaded]
  );
  const { background, contentColor } = useAvatarColors(parentBg, gradient);
  const avatarTokens = theme?.avatarV2;
  const avatarSize = avatarTokens?.size[size] ?? 48;
  const borderRadius = avatarTokens?.borderRadius[shape]?.size[size] ?? 12;
  return /* @__PURE__ */ jsx(AvatarContext.Provider, { value, children: /* @__PURE__ */ jsx(
    AvatarBox,
    {
      background: imageLoaded ? void 0 : background,
      textColor: contentColor,
      size: avatarSize,
      borderRadius,
      onClick,
      ...rest,
      children
    }
  ) });
};
const AvatarImage = (props) => {
  const {
    src,
    alt,
    onError
  } = props;
  const { size, shape, setImageLoaded, theme } = useAvatarContext();
  const avatarTokens = theme?.avatarV2;
  const borderRadius = (shape && size && avatarTokens?.borderRadius[shape]?.size[size]) ?? 12;
  const { loaded } = useImageLoad(src, onError, setImageLoaded);
  if (!src) {
    return null;
  }
  return /* @__PURE__ */ jsx(AvatarImageContainer, { borderRadius, isLoaded: !!loaded, children: /* @__PURE__ */ jsx(
    PlaceholderImg,
    {
      src,
      alt
    }
  ) });
};
const AvatarFallback = (props) => {
  const {
    color,
    text,
    icon
  } = props;
  const { size, theme } = useAvatarContext();
  const avatarTokens = theme?.avatarV2;
  const typography = (size && avatarTokens?.typography[size]) ?? "button1";
  const iconSize = (size && avatarTokens?.icon[size]) ?? "l";
  const letterCount = (size && avatarTokens?.letterCount[size]) ?? 2;
  const abbreviation = typeof text === "string" && abbreviate(text, typeof letterCount === "number" ? letterCount : 2);
  if (icon && React.isValidElement(icon)) {
    return /* @__PURE__ */ jsx(Icon, { icon, size: iconSize, color });
  }
  return /* @__PURE__ */ jsx(Typography, { variant: typography, color, children: abbreviation });
};
const Avatar = {
  Root: AvatarRoot,
  Image: AvatarImage,
  Fallback: AvatarFallback,
  SlotBadge,
  SlotBadgeOnline,
  SlotIcon,
  SlotAvatar
};
var Avatar_default = Avatar;
export {
  Avatar,
  Avatar_default as default
};
